import { ComponentType, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';

import { CustomSelect } from '../../shared/uiComponents';
import { Container } from '../../components/StyledComponents';
import { AdminTypes } from '../../components/Menu';

import MBHReport from './BillingReports/MBHReport';
import NonCredentialedReport from './BillingReports/NonCredentialedReport';
import NonBillableReport from './BillingReports/NonBillable';
import GraphReports from './GraphReports';
import DownloadSoapNotes from './SoapNotes';
import UtilizationReport from './UtilizationReport';
import PendingDSU from './PendingDSU';
import ExpiredSessions from './ExpiredSessions';
import AssessmentReport from './AssessmentReport';
import LimitedPermit from './LimitedPermit';
import BackdatedSessions from './BackdatedSessionsReport';
import { CancellationReport, ScheduleAllotmentReport, ScheduleReport, ScheduleUtilizationReport } from './ScheduleReport';

import { DispatchProperties, useSelector } from '../../redux/store';
import { clearFilterData } from '../../redux/State/clientSlice/billingSlice';
import AuthorizationReport from './AuthorizationReport';
import DOEReport from './DOEReport';
import ElcAttendanceReport from './ElcAttendanceReport';
import ElcGraphReport from './ElcGraphReport';
import { Chart } from '../../shared/Assets/menuIcons';
import { PageHeader } from '../../components/PageHeader';

type ReportConfig = Array<{ Component: ComponentType; path: string }>;

const ReportComponentsByRole: Record<number, ReportConfig> = {
	[AdminTypes.admin]: [
		{ path: 'MBH', Component: MBHReport },
		{ path: 'Not Credentialed', Component: NonCredentialedReport },
		{ path: 'Not Billable', Component: NonBillableReport },
		{ path: 'Graph', Component: GraphReports },
		{ path: 'SOAP Notes', Component: DownloadSoapNotes },
		{ path: 'Utilization', Component: UtilizationReport },
		{ path: 'Pending DSU', Component: PendingDSU },
		{ path: 'Expired Sessions', Component: ExpiredSessions },
		{ path: 'Assessment', Component: AssessmentReport },
		{ path: 'Limited Permit', Component: LimitedPermit },
		{ path: 'Backdated Sessions', Component: BackdatedSessions },
		{ path: 'Authorization', Component: AuthorizationReport },
		{ path: 'Sessions Report', Component: ScheduleReport },
		{ path: 'Cancellations', Component: CancellationReport },
		{ path: 'Schedule Adherence', Component: ScheduleUtilizationReport },
		{ path: 'Schedule Allotment', Component: ScheduleAllotmentReport },
		{ path: 'DOE', Component: DOEReport },
		{ path: 'ELC Attendance', Component: ElcAttendanceReport },
		{ path: 'ELC Graph', Component: ElcGraphReport },
	],
	[AdminTypes.bcba]: [
		{ path: 'Graph', Component: GraphReports },
		{ path: 'Assessment', Component: AssessmentReport },
		{ path: 'ELC Attendance', Component: ElcAttendanceReport },
		{ path: 'ELC Graph', Component: ElcGraphReport },
	],
	[AdminTypes.cs]: [
		{ path: 'Graph', Component: GraphReports },
		{ path: 'Assessment', Component: AssessmentReport },
		{ path: 'Utilization', Component: UtilizationReport },
		{ path: 'Authorization', Component: AuthorizationReport },
		{ path: 'Sessions Report', Component: ScheduleReport },
		{ path: 'Cancellations', Component: CancellationReport },
		{ path: 'Schedule Adherence', Component: ScheduleUtilizationReport },
	],
};

const Reports = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [, setParams] = useSearchParams();

	const role = useSelector((state) => state.account.role);
	const [selectedReport, setSelectedReport] = useState<string>('0');

	const config = useMemo(() => {
		const roleId = role?.section.id;

		return (roleId !== undefined && ReportComponentsByRole[roleId]) || [];
	}, [role?.section.id]);

	const dropdownData = useMemo(
		() => config.map((item, index) => ({ id: String(index), name: item.path })),
		[config],
	);

	const handleReportChange = useCallback(
		(event: SelectChangeEvent<string>) => {
			dispatch(clearFilterData());
			window.localStorage.removeItem('tab');
			setParams(new URLSearchParams(), { replace: true });
			setSelectedReport(event.target.value);
		},
		[dispatch, setParams],
	);

	const selectedIndex = Number(selectedReport);
	const Panel = config[selectedIndex]?.Component;

	return (
		<Container>
			<PageHeader icon={<Chart />} title="Reports" isInContainer customStyles={{ marginBottom: '24px' }} />
			<div className="marginBottom16" style={{ maxWidth: '300px' }}>
				<CustomSelect
					label="Report:"
					data={dropdownData}
					value={selectedReport}
					setValue={handleReportChange}
				/>
			</div>
			{Panel && <Panel />}
		</Container>
	);
};

export default Reports;
